import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@300;400;500;600&display=swap');

:root {
  --bg: #0a0a0a;
  --surface: #111111;
  --surface2: #181818;
  --border: #2a2a2a;
  --accent: #e8ff3c;
  --accent-dim: rgba(232, 255, 60, 0.10);
  --text: #f0f0f0;
  --muted: #777777;
  --red: #ff4545;
}

* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

.auth-page {
  min-height: 100vh;
  width: 100%;
  background: var(--bg);
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'DM Sans', sans-serif;
  padding: 2rem 1rem;
  position: relative;
  overflow: hidden;
}

/* Background grid */
.auth-page::before {
  content: '';
  position: absolute;
  inset: 0;

  background-image:
    linear-gradient(rgba(255,255,255,0.035) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,0.035) 1px, transparent 1px);

  background-size: 48px 48px;
  pointer-events: none;
}

/* Subtle glow */
.auth-page::after {
  content: '';
  position: absolute;
  width: 500px;
  height: 500px;
  background: var(--accent);
  opacity: 0.025;
  filter: blur(120px);
  border-radius: 50%;
  pointer-events: none;
}

.auth-card {
  position: relative;
  z-index: 1;

  width: 100%;
  max-width: 420px;

  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 12px;

  padding: 2.8rem 2.5rem;

  box-shadow:
    0 25px 80px rgba(0, 0, 0, 0.45),
    0 0 60px rgba(232, 255, 60, 0.025);
}

/* =========================
   LOGO
========================= */

.auth-logo {
  display: block;

  font-family: 'Bebas Neue', sans-serif;
  font-size: 2.6rem;
  line-height: 1;

  letter-spacing: 0.12em;

  color: var(--accent);
  text-align: center;

  margin-bottom: 0.4rem;
}

.landing-tagline {
  text-align: center;
  color: var(--muted);
  font-size: 0.85rem;
  line-height: 1.5;
  margin-bottom: 2.2rem;
}

/* =========================
   GUEST MODE — the primary, highlighted action on this page
========================= */

.guest-highlight-wrap {
  width: 100%;
  padding: 4px;
  border-radius: 14px;

  background: linear-gradient(135deg, rgba(232,255,60,0.55), rgba(232,255,60,0.08));

  box-shadow: 0 0 40px rgba(232, 255, 60, 0.10);
}

.guest-highlight-btn {
  width: 100%;
  min-height: 64px;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;

  background: var(--surface2);
  border: 1px solid rgba(232, 255, 60, 0.5);
  border-radius: 11px;

  padding: 1rem 1.25rem;

  cursor: pointer;

  transition:
    background 0.2s ease,
    border-color 0.2s ease,
    transform 0.15s ease,
    box-shadow 0.2s ease;
}

.guest-highlight-btn:hover {
  background: var(--accent-dim);
  border-color: var(--accent);
  transform: translateY(-2px);
  box-shadow: 0 10px 30px rgba(232, 255, 60, 0.18);
}

.guest-highlight-btn:active {
  transform: translateY(0);
}

.guest-highlight-title {
  font-family: 'Bebas Neue', sans-serif;
  font-size: 1.25rem;
  letter-spacing: 0.06em;
  color: var(--accent);
}

.guest-highlight-sub {
  font-family: 'DM Sans', sans-serif;
  font-size: 0.78rem;
  color: var(--muted);
}

/* =========================
   DIVIDER
========================= */

.auth-divider-row {
  display: flex;
  align-items: center;
  gap: 0.9rem;

  margin: 1.7rem 0;
}

.auth-divider-row .line {
  flex: 1;
  height: 1px;
  background: var(--border);
}

.auth-divider-row span {
  font-size: 0.72rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--muted);
}

/* =========================
   BOTTOM LINKS — Create account / Sign in
========================= */

.landing-links {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;

  text-align: center;
}

.landing-link-btn {
  flex: 1;

  background: transparent;
  color: var(--text);

  border: 1px solid var(--border);
  border-radius: 6px;

  padding: 0.78rem 1rem;

  font-family: 'DM Sans', sans-serif;
  font-size: 0.85rem;
  font-weight: 500;
  letter-spacing: 0.02em;

  text-decoration: none;

  display: flex;
  align-items: center;
  justify-content: center;

  cursor: pointer;

  transition:
    background 0.2s ease,
    border-color 0.2s ease,
    color 0.2s ease,
    transform 0.15s ease;
}

.landing-link-btn:hover {
  background: var(--surface2);
  border-color: var(--accent);
  color: var(--accent);
  transform: translateY(-1px);
}

/* =========================
   INPUTS (guest email modal)
========================= */

.auth-input {
  width: 100%;
  height: 48px;

  background: var(--surface2);

  border: 1px solid var(--border);
  border-radius: 6px;

  color: var(--text);

  font-family: 'DM Sans', sans-serif;
  font-size: 0.88rem;

  padding: 0 1rem;

  outline: none;

  transition:
    border-color 0.2s ease,
    background 0.2s ease,
    box-shadow 0.2s ease;
}

.auth-input:hover {
  border-color: #3a3a3a;
}

.auth-input:focus {
  background: #1b1b1b;
  border-color: var(--accent);
  box-shadow: 0 0 0 3px var(--accent-dim);
}

.auth-input::placeholder {
  color: #5f5f5f;
}

.auth-title {
  font-family: 'Bebas Neue', sans-serif;
  font-size: 1.7rem;
  line-height: 1;
  letter-spacing: 0.07em;
  color: var(--text);
  text-align: center;
  margin-bottom: 0.5rem;
}

.auth-subtitle {
  text-align: center;
  color: var(--muted);
  font-size: 0.82rem;
  line-height: 1.5;
  margin-bottom: 1.5rem;
}

.auth-btn {
  width: 100%;
  min-height: 48px;

  background: var(--accent);
  color: #000;

  border: 1px solid var(--accent);
  border-radius: 6px;

  padding: 0.78rem 1.5rem;

  font-family: 'Bebas Neue', sans-serif;
  font-size: 1.1rem;

  letter-spacing: 0.08em;

  cursor: pointer;

  display: flex;
  align-items: center;
  justify-content: center;

  gap: 0.5rem;

  transition:
    background 0.2s ease,
    border-color 0.2s ease,
    transform 0.15s ease,
    box-shadow 0.2s ease;
}

.auth-btn:hover {
  background: #f0ff61;
  border-color: #f0ff61;
  transform: translateY(-1px);
  box-shadow: 0 8px 25px rgba(232, 255, 60, 0.12);
}

/* =========================
   GUEST MODAL
========================= */

.modal-overlay {
  position: fixed;
  inset: 0;

  background: rgba(0, 0, 0, 0.86);

  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);

  z-index: 1000;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 1rem;
}

.modal-box {
  width: 100%;
  max-width: 400px;

  background: var(--surface);

  border: 1px solid var(--border);
  border-radius: 12px;

  padding: 2.5rem;

  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.65);
}

.modal-actions {
  display: flex;
  gap: 0.8rem;

  margin-top: 1.5rem;
}

.btn-cancel,
.btn-continue {
  flex: 1;
}

.btn-cancel {
  background: transparent;
  color: var(--text);

  border: 1px solid var(--border);
  border-radius: 6px;

  padding: 0.78rem 1.5rem;

  font-family: 'Bebas Neue', sans-serif;
  font-size: 1.05rem;

  letter-spacing: 0.08em;

  cursor: pointer;

  transition: background 0.2s ease, border-color 0.2s ease;
}

.btn-cancel:hover {
  background: var(--surface2);
  border-color: #444;
}

.btn-continue {
  background: var(--accent);
  color: #000;

  border: 1px solid var(--accent);
  border-radius: 6px;

  padding: 0.78rem 1.5rem;

  font-family: 'Bebas Neue', sans-serif;
  font-size: 1.05rem;

  letter-spacing: 0.08em;

  cursor: pointer;

  transition: background 0.2s ease, transform 0.15s ease;
}

.btn-continue:hover {
  background: #f0ff61;
  transform: translateY(-1px);
}

/* =========================
   MOBILE
========================= */

@media (max-width: 480px) {
  .auth-page {
    padding: 1rem;
  }

  .auth-card {
    padding: 2.2rem 1.4rem;
    border-radius: 10px;
  }

  .auth-logo {
    font-size: 2.2rem;
  }

  .landing-links {
    flex-direction: column;
  }

  .modal-box {
    padding: 2rem 1.4rem;
  }

  .modal-actions {
    flex-direction: column;
  }
}
`;

const Landings = () => {
  const navigate = useNavigate();

  const [isGuestModalOpen, setIsGuestModalOpen] = useState(false);
  const [guestEmail, setGuestEmail] = useState("");

  const handleGuestSubmit = (e) => {
    e.preventDefault();

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(guestEmail)) {
      toast.error("Please enter a valid email address.");
      return;
    }

    localStorage.setItem("guestEmail", guestEmail);
    setIsGuestModalOpen(false);
    toast.success("Continuing as guest...");
    navigate("/guest");
  };

  return (
    <>
      <style>{styles}</style>
      <div className="auth-page">
        <div className="auth-card">
          <span className="auth-logo">MUSICA</span>
          <p className="landing-tagline">Your music, your way.</p>

          {/* Primary action: Guest Mode */}
          <div className="guest-highlight-wrap">
            <button
              type="button"
              className="guest-highlight-btn"
              onClick={() => setIsGuestModalOpen(true)}
            >
              <span className="guest-highlight-title">Continue as Guest</span>
              <span className="guest-highlight-sub">No need to log in</span>
            </button>
          </div>

          <div className="auth-divider-row">
            <span className="line" />
            <span>or</span>
            <span className="line" />
          </div>

          {/* Secondary actions: create account / sign in, each its own page */}
          <div className="landing-links">
            <a href="/Registration" className="landing-link-btn">
              Create account
            </a>
            <a href="/Login" className="landing-link-btn">
              Sign in
            </a>
          </div>
        </div>
      </div>

      {/* --- GUEST MODAL OVERLAY --- */}
      {isGuestModalOpen && (
        <div className="modal-overlay" onClick={() => setIsGuestModalOpen(false)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <h2 className="auth-title">
              Continue as <span style={{ color: "var(--accent)" }}>Guest</span>
            </h2>
            <p className="auth-subtitle">
              Enter your email so we can send your receipts and download links after purchase.
            </p>

            <form onSubmit={handleGuestSubmit}>
              <div style={{ marginBottom: "1.2rem" }}>
                <input
                  type="email"
                  required
                  className="auth-input"
                  placeholder="guest@example.com"
                  value={guestEmail}
                  onChange={(e) => setGuestEmail(e.target.value)}
                  autoFocus
                />
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setIsGuestModalOpen(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="auth-btn btn-continue">
                  Continue
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default Landings;